
import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, NotFoundState, LoadingScreen, ErrorState } from "@bi-studio/shared/components";
import { LoadingOverlay } from "@mantine/core";
import { getFolderForItem } from "@bi-studio/shared/apis";

import { PageActions, ChartActionsButton, ChartView, ItemDetailModal, ChartVariablesBar } from "@/components";
import { PreviewVariablesModal } from "@/components/reports";
import { PREDEFINED_VARIABLES } from "@bi-studio/shared/lib";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
    fetchChartDetails,
    executeChartData,
    setVariableValues,
    resetChartState,
    selectChart,
    selectReport,
    selectChartData,
    selectChartLoading,
    selectChartExecuting,
    selectChartError,
    selectVariableValues
} from "@/store/slices/chartSlice";


export default function ChartDetailPage() {

    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id: chartId } = useParams();
    const [searchParams] = useSearchParams();
    const isEmbed = searchParams.get("embed") === "true";

    // Redux selectors
    const chart = useAppSelector(selectChart);
    const report = useAppSelector(selectReport);
    const chartData = useAppSelector(selectChartData);
    const isLoading = useAppSelector(selectChartLoading);
    const isExecuting = useAppSelector(selectChartExecuting);
    const error = useAppSelector(selectChartError);
    const variableValues = useAppSelector(selectVariableValues);

    const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
    const [isVariablesModalOpen, setIsVariablesModalOpen] = React.useState(false);


    const handleLogout = React.useCallback(() => {
        navigate("/login");
    }, [navigate]);

    React.useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate("/login");
        }
    }, [isAuthenticated, authLoading, navigate]);

    // Initial load and cleanup
    React.useEffect(() => {
        if (token && chartId) {
            dispatch(fetchChartDetails({ token, chartId }));
        }
        return () => {
            dispatch(resetChartState());
        };
    }, [token, chartId, dispatch]);

    // Listen for chart updates from modal
    React.useEffect(() => {
        const handleChartUpdate = (event: CustomEvent<{ chartId: string }>) => {
            if (event.detail.chartId === chartId && token) {
                dispatch(fetchChartDetails({ token, chartId }));
            }
        };

        window.addEventListener("chart-updated", handleChartUpdate as EventListener);
        return () => {
            window.removeEventListener(
                "chart-updated",
                handleChartUpdate as EventListener
            );
        };
    }, [chartId, token, dispatch]);

    // Execute report when ready
    const runChart = React.useCallback(async (values: Record<string, string> = {}) => {
        if (!chartId || !token || !chart) return;
        dispatch(executeChartData({ token, reportId: chart.reportId, variables: values }));
        setIsVariablesModalOpen(false);
    }, [chartId, token, chart, dispatch]);

    React.useEffect(() => {
        if (chartId && token && chart && report && !isLoading && !isExecuting) {
            const userVariables = (report.variables || []).filter(v => !PREDEFINED_VARIABLES.includes(v.name));

            if (userVariables.length > 0 && Object.keys(variableValues).length === 0) {
                setIsVariablesModalOpen(true);
                return;
            }

            // Only run if chartData is empty or if it's the first load
            if (chartData.length === 0) {
                runChart(variableValues);
            }
        }
    }, [chartId, token, chart, report, isLoading, isExecuting, runChart, variableValues, chartData.length]);



    const handleViewDetails = () => {
        setIsDetailsOpen(true);
    };

    const handleBack = React.useCallback(async () => {
        if (!token || !chartId) {
            navigate(-1);
            return;
        }

        try {
            const folderInfo = await getFolderForItem(token, "chart", chartId);
            if (folderInfo.folderId) {
                navigate(`/folders/${folderInfo.folderId}`);
            } else {
                navigate("/folders");
            }
        } catch (err) {
            console.error("Failed to get folder for chart:", err);
            navigate(-1);
        }
    }, [token, chartId, navigate]);

    const isReady = !!chart;
    const isPageLoading = isLoading || authLoading;

    if (!isAuthenticated) {
        return null;
    }

    if (!isReady && isPageLoading) {
        return (
            <AppLayout onLogout={handleLogout}>
                <LoadingScreen message="Loading chart..." />
            </AppLayout>
        );
    }

    if (!chart) {
        const fallback = (
            <NotFoundState
                title="Chart Not Found"
                message="The chart you are looking for does not exist or you don't have permission to view it."
                showAction={!isEmbed}
                actionLabel="Back to Folders"
                onAction={handleBack}
            />
        );
        return isEmbed ? fallback : <AppLayout onLogout={handleLogout}>{fallback}</AppLayout>;
    }

    const content = (
        <div className={`space-y-4 ${isEmbed ? "p-2" : ""}`} style={{ position: "relative" }}>
            <LoadingOverlay visible={isLoading && isReady} />
            <PageActions title={chart.config.title || `Chart ${chart.id}`} onBack={!isEmbed ? handleBack : undefined}>
                {!isEmbed && (
                    <ChartActionsButton
                        chartId={chartId!}
                        report={report}
                        variableValues={variableValues}
                        isLoading={isLoading}
                        isExecuting={isExecuting}
                        runChart={runChart}
                        setIsVariablesModalOpen={setIsVariablesModalOpen}
                        handleViewDetails={handleViewDetails}
                    />
                )}
            </PageActions>

            {error && (
                <ErrorState title="Error" message={error} showAction={false} />
            )}

            {!isVariablesModalOpen && (
                <ChartVariablesBar onRun={(values) => runChart(values)} />
            )}

            {chart && (
                <ChartView
                    chart={chart as any}
                    chartData={chartData}
                    isExecuting={isExecuting}
                />
            )}
        </div>
    );

    const renderContent = () => (
        <>
            {content}
            <ItemDetailModal
                itemId={chartId ?? null}
                itemType="chart"
                opened={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
            />

            {report && (
                <PreviewVariablesModal
                    opened={isVariablesModalOpen}
                    onClose={() => setIsVariablesModalOpen(false)}
                    variables={report.variables || []}
                    values={variableValues}
                    onValuesChange={(vals) => dispatch(setVariableValues(vals))}
                    onConfirm={() => runChart(variableValues)}
                    dataSourceId={report.dataSourceId}
                    loading={isExecuting}
                />
            )}
        </>
    );

    if (isEmbed) {
        return renderContent();
    }

    return (
        <AppLayout onLogout={handleLogout}>
            {renderContent()}
        </AppLayout>
    );
}
